import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronUp } from 'react-icons/fi';
import type { RootState } from '../../store';
import IconWrapper from '../common/IconWrapper';
import { DOCK_LIMITS, DockPosition, setDockPrefs, useDockPrefs } from './dockPrefs';

/**
 * 노드 속성 영역을 캔버스 **오른쪽** 또는 **아래**에 붙인다 — 크기 조절 손잡이와 접기를 함께 맡는다.
 *
 *   · 손잡이를 끌면 폭(오른쪽)·높이(아래)가 바뀌고, 두 번 누르면 기본 크기로 돌아간다
 *   · 접으면 얇은 막대만 남는다 — 노드를 고르면 저절로 다시 펼친다(속성을 보려고 고른 것이므로)
 *   · 아래 배치에서는 속성 섹션이 폭에 맞춰 여러 칸으로 흐른다(index.css 의 .prop-dock--bottom)
 */
const PropertyDock: React.FC<{ position: DockPosition; children: React.ReactNode }> = ({ position, children }) => {
  const { t } = useTranslation();
  const prefs = useDockPrefs();
  const selectedNodeId = useSelector((s: RootState) => s.workflow.selectedNodeId);
  const bottom = position === 'bottom';

  // 노드를 새로 고르면 펼친다
  const prevSel = useRef(selectedNodeId);
  useEffect(() => {
    if (selectedNodeId && selectedNodeId !== prevSel.current && prefs.collapsed) setDockPrefs({ collapsed: false });
    prevSel.current = selectedNodeId;
  }, [selectedNodeId, prefs.collapsed]);

  /** 손잡이 끌기 — 오른쪽이면 왼쪽으로 끌수록 넓어지고, 아래면 위로 끌수록 높아진다 */
  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const start = bottom ? e.clientY : e.clientX;
    const from = bottom ? prefs.height : prefs.width;
    const maxH = Math.round(window.innerHeight * DOCK_LIMITS.maxHeightRatio);
    const move = (ev: MouseEvent) => {
      const delta = start - (bottom ? ev.clientY : ev.clientX);
      if (bottom) setDockPrefs({ height: Math.max(DOCK_LIMITS.minHeight, Math.min(maxH, from + delta)) });
      else setDockPrefs({ width: Math.max(DOCK_LIMITS.minWidth, Math.min(DOCK_LIMITS.maxWidth, from + delta)) });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = bottom ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';     // 끄는 동안 글자가 선택되지 않게
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [bottom, prefs.height, prefs.width]);

  const resetSize = () => setDockPrefs(bottom ? { height: 280 } : { width: 400 });

  if (prefs.collapsed) {
    // 접힌 막대 — 누르면 펼친다
    return (
      <button
        type="button"
        onClick={() => setDockPrefs({ collapsed: false })}
        title={t('layout:dock.expand')}
        className={bottom
          ? 'shrink-0 h-8 w-full flex items-center gap-2 px-3 border-t border-gray-200 bg-gray-100 text-xs text-gray-600 hover:bg-gray-200'
          : 'shrink-0 w-8 h-full flex flex-col items-center gap-2 pt-3 border-l border-gray-200 bg-gray-100 text-xs text-gray-600 hover:bg-gray-200'}
      >
        <IconWrapper icon={bottom ? FiChevronUp : FiChevronLeft} size={16} />
        <span className={bottom ? '' : '[writing-mode:vertical-rl]'}>{t('layout:side.props')}</span>
      </button>
    );
  }

  const handle = (
    <div
      role="separator"
      aria-orientation={bottom ? 'horizontal' : 'vertical'}
      title={t('layout:dock.resize')}
      onMouseDown={startDrag}
      onDoubleClick={resetSize}
      className={bottom
        ? 'shrink-0 h-1.5 w-full cursor-row-resize bg-gray-200 hover:bg-blue-400 transition-colors'
        : 'shrink-0 w-1.5 h-full cursor-col-resize bg-gray-200 hover:bg-blue-400 transition-colors'}
    />
  );

  return bottom ? (
    <div className="shrink-0 w-full flex flex-col min-h-0 prop-dock--bottom" style={{ height: prefs.height }}>
      {handle}
      <div className="flex-1 min-h-0 overflow-hidden bg-white">{children}</div>
    </div>
  ) : (
    <div className="shrink-0 h-full flex min-w-0 prop-dock--right" style={{ width: prefs.width }}>
      {handle}
      <div className="flex-1 min-w-0 h-full min-h-0 overflow-hidden bg-white">{children}</div>
    </div>
  );
};

export default PropertyDock;
